import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { BrandVisualKit } from 'types'
import { buildErrorMessage } from 'utils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { reportError } from 'utils/error'

export interface Variables extends Partial<Omit<BrandVisualKit, 'id' | 'workspace_id'>> {
  visual_style_id?: number | null
}

interface Props {
  id: number
}

interface Data {
  brand_visual_kit: BrandVisualKit
}

interface Context {
  previousBrandVisualKit?: BrandVisualKit
}

export const updateBrandVisualKit = async ({
  id,
  workspaceId,
  ...variables
}: Props & Variables & { workspaceId: number }) => {
  const response = await sendToApi<Data>(`/w/${workspaceId}/brand_visual_kits/${id}`, {
    method: 'PATCH',
    body: { ...variables },
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    reportError('Failed to update brand visual kit', {
      metaData: {
        id,
        variables,
        response: response.data,
      },
    })
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data
}

export const useUpdateBrandVisualKit = ({ id }: Props) => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<Data, Error, Variables, Context>(
    ['workspaces', currentWorkspaceId, 'brand_visual_kits', id],
    (variables) => {
      return updateBrandVisualKit({ id, workspaceId: currentWorkspaceId, ...variables })
    },
    {
      onMutate: async (variables) => {
        await queryClient.cancelQueries(['workspaces', currentWorkspaceId, 'brand_visual_kits', id])
        const previousBrandVisualKit = queryClient.getQueryData([
          'workspaces',
          currentWorkspaceId,
          'brand_visual_kits',
          id,
        ]) as BrandVisualKit | undefined
        queryClient.setQueryData(['workspaces', currentWorkspaceId, 'brand_visual_kits', id], (old: any) => ({
          ...(old as Variables),
          ...variables,
        }))
        return { previousBrandVisualKit }
      },
      onError: (_, __, context) => {
        queryClient.setQueryData(
          ['workspaces', currentWorkspaceId, 'brand_visual_kits', id],
          context?.previousBrandVisualKit,
        )
      },
      onSettled: async () => {
        await Promise.all([
          queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brand_visual_kits', 'list']),
          queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brand_visual_kits', id]),
          queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brandKits']),
        ])
      },
    },
  )
}
